/**
 * Tests network speed by downloading a test file and measuring the time and data transferred.
 * Adds a network quality class to <html>: slow, good, fast
 *
 * @param {object} options Configuration options for the speed test
 * @param {string} options.testUrl - URL of the file to download for speed testing
 * @param {number} options.duration - Maximum duration in seconds for the test (default: 10)
 * @param {boolean} options.measureLatency - Whether to measure latency/ping (default: true)
 * @returns {Promise<object>} Object containing speed test results
 */
export async function testNetworkSpeed(options = {}) {
	const {
		testUrl = 'https://speed.cloudflare.com/__down?bytes=2000000', // ~2MB (better for mobile stability)
		duration = 8,
		measureLatency = true
	} = options;

	console.log('starting network speed test');

	const results = {
		downloadSpeed: null, // Mbps
		latency: null, // ms
		bytesDownloaded: 0,
		duration: 0,
		error: null,
		networkClass: null
	};

	function applyNetworkClass({ downloadSpeed, latency, duration }) {
		const root = document.documentElement;

		// remove existing classes
		root.classList.remove('slow', 'good', 'fast');

		// if test is unreliable or failed → assume "good" (not worst-case)
		if (!downloadSpeed || duration < 0.5) {
			root.classList.add('good');
			return 'good';
		}

		let label;

		// MOBILE-REALISTIC CLASSIFICATION
		if (downloadSpeed < 3) {
			label = 'slow';
		} else if (downloadSpeed < 10 || latency > 300) {
			label = 'good';
		} else {
			label = 'fast';
		}

		root.classList.add(label);
		return label;
	}

	try {
		// Measure latency
		if (measureLatency) {
			const latencyStart = performance.now();
			await fetch(testUrl, { method: 'HEAD', cache: 'no-store' });
			results.latency = Math.round(performance.now() - latencyStart);
		}

		// Measure download speed
		const startTime = performance.now();
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), duration * 1000);

		const response = await fetch(testUrl, {
			signal: controller.signal,
			cache: 'no-store'
		});

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const reader = response.body.getReader();
		let bytesReceived = 0;

		while (true) {
			const { done, value } = await reader.read();
			if (done) break;
			bytesReceived += value.length;
		}

		clearTimeout(timeoutId);

		const endTime = performance.now();
		const durationSeconds = (endTime - startTime) / 1000;

		results.bytesDownloaded = bytesReceived;
		results.duration = durationSeconds;

		// Calculate Mbps
		const bitsDownloaded = bytesReceived * 8;
		results.downloadSpeed = parseFloat(
			(bitsDownloaded / durationSeconds / 1_000_000).toFixed(2)
		);

		// Apply classification
		results.networkClass = applyNetworkClass(results);

		console.log(`Network Speed Test Results:
- Download Speed: ${results.downloadSpeed} Mbps
- Latency: ${results.latency} ms
- Data Downloaded: ${(bytesReceived / 1_000_000).toFixed(2)} MB
- Test Duration: ${durationSeconds.toFixed(2)} seconds
- Network Class: ${results.networkClass}`);

	} catch (error) {
		if (error.name === 'AbortError') {
			results.error = 'Test timeout reached';
		} else {
			results.error = error.message;
			console.error('Network speed test error:', error);
		}

		// fallback → don't assume "slow", that's too aggressive
		results.networkClass = applyNetworkClass({
			downloadSpeed: 0,
			latency: 999,
			duration: 0
		});
	}

	return results;
}

export default testNetworkSpeed;
